import {
  AfterContentInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Wraps one form control and owns the wiring every field in this codebase was
 * missing.
 *
 * Measured before this existed: 173 inputs, 155 formControlName bindings, 44
 * <label> elements and 18 `for=` associations. aria-describedby appeared zero
 * times, so all 234 inline validation messages were visible but unreachable -
 * a sighted user saw "Field Required", a screen-reader user heard nothing.
 *
 * The control keeps living in the parent's FormGroup; this only projects it and
 * decorates it, so it can be adopted field by field without touching form
 * submission.
 *
 * The label is deliberately NOT taken straight from a runtime CMS string with
 * no fallback. That mistake has been made four times on this site: the name
 * disappears whenever the fetch fails. `label` wins, `srLabel` (usually the
 * placeholder) is next, and there is always a static last resort.
 */
@Component({
  selector: 'raksme-form-field',
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.css']
})
export class FormFieldComponent
  implements OnInit, OnChanges, AfterContentInit, OnDestroy {

  /** The control this field describes. Required - the wiring depends on it. */
  @Input() control: AbstractControl;

  /** Unique within the page; used for id/for/aria-describedby. */
  @Input() fieldId: string;

  /** Visible label. When absent the field stays visually as it was. */
  @Input() label: string;

  /** Accessible name when there is no visible label - usually the placeholder. */
  @Input() srLabel: string;

  /** Last-resort name so the field is never anonymous, even with the API down. */
  @Input() fallbackLabel: string = 'Field';

  @Input() required: boolean = false;

  /**
   * The page renders its own <label> inside this field; wire it instead of
   * drawing a second one.
   *
   * my-account/profile is the case this exists for. Its 16 labels carry
   * `.label-title` and `.with-icon` rules that live in the *component's* own
   * stylesheet, and view encapsulation means those rules cannot reach a label
   * rendered from this component's template - re-homing the label here would
   * quietly restyle every field on the page (600 -> bold, 14px -> inherited,
   * and the four icon rows would lose their flex layout entirely).
   *
   * So the page keeps its markup and this component supplies only what the
   * label was missing: the `for` association, and the error wiring below.
   */
  @Input() projectedLabel: boolean = false;

  /**
   * Whether the surrounding form has been submitted.
   *
   * Several forms reveal their errors on submit as well as on blur - the
   * profile form's own condition is `(invalid && touched) || (submitted &&
   * invalid)`. Without this input those errors would still be visible but
   * would lose their `aria-describedby` link on exactly the pass where the
   * user most needs it: the one where they pressed Save.
   */
  @Input() submitted: boolean = false;

  /**
   * An error owned by the FormGroup rather than by this control.
   *
   * Password confirmation is the case this exists for: `misMatch` is set on
   * the form, and *both* controls are individually valid when it fires. A
   * control-only `showErrors` would hide the single message that explains why
   * the form will not submit, so the caller passes the form's own flag in.
   */
  @Input() groupError: boolean = false;

  private native: HTMLElement = null;
  private statusSub: Subscription;

  constructor(private host: ElementRef) { }

  get errorId(): string { return (this.fieldId || 'field') + '-error'; }

  get accessibleName(): string {
    const pick = [this.label, this.srLabel, this.fallbackLabel]
      .find(v => v && String(v).trim());
    return pick ? String(pick).trim() : 'Field';
  }

  get showErrors(): boolean {
    if (this.groupError) { return true; }
    return !!this.control && this.control.invalid
      && (this.control.touched || this.submitted);
  }

  ngOnInit(): void {
    if (this.control) {
      // The projected control is a plain element; nothing re-renders it when
      // validity changes, so the attributes are refreshed by hand.
      this.statusSub = this.control.statusChanges.subscribe(() => this.sync());
    }
  }

  /**
   * `groupError` and `submitted` can both turn this field invalid without the
   * control's own status ever changing - a password mismatch lives on the form
   * and leaves both controls VALID, and pressing Save changes no control at
   * all. statusChanges therefore never fires for either, so without this the
   * error box appeared correctly but `aria-describedby` was never set on the
   * control: the message was on screen and still unreachable, which is the
   * exact gap this component exists to close. Caught in the browser, not by
   * the build.
   */
  ngOnChanges(): void {
    this.sync();
  }

  ngAfterContentInit(): void {
    this.native = this.host.nativeElement.querySelector('input, select, textarea');
    if (this.native && this.fieldId) {
      this.native.setAttribute('id', this.fieldId);
    }
    // When the page brought its own label, this component rendered none, so the
    // only <label> under the host is that one.
    if (this.projectedLabel && this.fieldId) {
      const projected = this.host.nativeElement.querySelector('label');
      if (projected) { projected.setAttribute('for', this.fieldId); }
    }
    this.sync();
  }

  /** Point the control at its error container, but only while one is showing. */
  private sync(): void {
    if (!this.native) { return; }
    if (this.showErrors) {
      this.native.setAttribute('aria-describedby', this.errorId);
      this.native.setAttribute('aria-invalid', 'true');
    } else {
      this.native.removeAttribute('aria-describedby');
      this.native.removeAttribute('aria-invalid');
    }
  }

  ngOnDestroy(): void {
    if (this.statusSub) { this.statusSub.unsubscribe(); }
  }
}
